import React from 'react';
import { Icon } from '@iconify/react';

export const sanitizeDurationAmount = (raw) => {
  const cleaned = String(raw).replace(/[^\d.,]/g, '');
  const match = cleaned.match(/^(\d*)([.,])?(\d*)/);
  if (!match) return '';
  const [, intPart, sep, frac] = match;
  if (!sep) return intPart;
  return `${intPart || '0'}${sep}${frac}`;
};

const parseAmount = (raw) => {
  const text = String(raw).trim();
  if (!text) return NaN;
  const normalized = text.replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) return NaN;
  return Number(normalized);
};

export const toEstimatedMinutes = (amount, unit = 'min') => {
  const value = parseAmount(amount);
  if (!Number.isFinite(value) || value < 0) return NaN;
  return unit === 'h' ? Math.round(value * 60) : Math.round(value);
};

export const formatDurationAmount = (minutes, unit = 'min') => {
  const value = Math.max(0, Math.round(Number(minutes) || 0));
  if (unit !== 'h') return String(value);
  const hours = Math.round((value / 60) * 100) / 100;
  return String(hours).replace('.', ',');
};

/** Heures si le total tombe juste (90 → 1,5 h), sinon des minutes. */
export const durationFieldFromMinutes = (total) => {
  const minutes = Math.max(0, Math.round(Number(total) || 0));
  if (minutes > 0 && minutes % 6 === 0) {
    return { amount: formatDurationAmount(minutes, 'h'), unit: 'h' };
  }
  return { amount: minutes ? String(minutes) : '', unit: 'min' };
};

/**
 * Champ durée : valeur + bascule min/h.
 * En heures, 1,5 et 1.5 valent 1 h 30.
 * @param {{
 *   value?: string,
 *   unit?: 'min'|'h',
 *   onChange?: (next: { amount: string, unit: 'min'|'h' }) => void,
 *   onKeyDown?: (e: React.KeyboardEvent) => void,
 *   invalid?: boolean,
 *   ariaLabel?: string,
 *   className?: string,
 *   prefix?: React.ReactNode,
 * }} props
 */
const TempsDurationInput = ({
  value = '',
  unit = 'min',
  onChange,
  onKeyDown,
  invalid = false,
  ariaLabel = 'Durée',
  className = '',
  prefix = null,
}) => {
  const rootClass = [
    'tp-duration',
    invalid ? 'is-invalid' : '',
    className,
  ].filter(Boolean).join(' ');

  const toggleUnit = () => {
    const nextUnit = unit === 'h' ? 'min' : 'h';
    const minutes = toEstimatedMinutes(value, unit);
    onChange?.({
      amount: Number.isFinite(minutes) ? formatDurationAmount(minutes, nextUnit) : value,
      unit: nextUnit,
    });
  };

  return (
    <div className={rootClass}>
      {prefix}
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange?.({
          amount: sanitizeDurationAmount(event.target.value),
          unit,
        })}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        aria-invalid={invalid}
      />
      <button
        type="button"
        className="tp-duration__unit"
        aria-label={`Unité : ${unit === 'h' ? 'heures' : 'minutes'} (cliquer pour changer)`}
        title="Basculer min / h"
        onClick={toggleUnit}
      >
        <span>{unit}</span>
        <Icon icon="mdi:unfold-more-horizontal" width={14} aria-hidden />
      </button>
    </div>
  );
};

export default TempsDurationInput;
